import React, { useState, useEffect, useRef } from 'react'
import { auth, db } from '../components/Login/firebase-config'
import { useNavigate, useLocation } from 'react-router-dom'
import { collection, getDocs, where, query, doc, setDoc, getDoc, addDoc, deleteDoc, runTransaction, serverTimestamp, onSnapshot } from 'firebase/firestore';
import styles from '../components/home/Home.module.css';
import GlobeSpinner from '../components/GlobeSpinner';
import { useUserData } from '../components/UserDataContext';
export let gametimeH

const levels = [
    { label: 'Beginner',     elo: 400,  icon: '♟', skill: 0,  depth: 1,  randomness: 0.65 },
    { label: 'Easy',         elo: 800,  icon: '♞', skill: 1,  depth: 2,  randomness: 0.35 },
    { label: 'Intermediate', elo: 1200, icon: '♜', skill: 4,  depth: 4,  randomness: 0.15 },
    { label: 'Advanced',     elo: 1600, icon: '♝', skill: 8,  depth: 6,  randomness: 0    },
    { label: 'Expert',       elo: 2000, icon: '♛', skill: 14, depth: 10, randomness: 0    },
    { label: 'Master',       elo: 2500, icon: '♚', skill: 20, depth: 15, randomness: 0    },
    { label: 'Stockfish',    elo: null, icon: '⚡', skill: 20, depth: 20, randomness: 0    },
];

export default function Home() {
    const { currentUser } = auth
    const navigate = useNavigate()
    const location = useLocation()
    const challengeUsername = location.state?.friendUsername || null;
    const newGameOptions = [
        { label: 'Black pieces', value: 'b' },
        { label: 'White pieces', value: 'w' },
        { label: 'Random', value: 'r' },
    ]
    const timePresets = [
        { cat: 'Bullet', opts: [{ l: '1+0', m: 1, i: 0 }, { l: '1+1', m: 1, i: 1 }, { l: '2+1', m: 2, i: 1 }] },
        { cat: 'Blitz',  opts: [{ l: '3+0', m: 3, i: 0 }, { l: '3+2', m: 3, i: 2 }, { l: '5+0', m: 5, i: 0 }, { l: '5+3', m: 5, i: 3 }] },
        { cat: 'Rapid',  opts: [{ l: '10+0', m: 10, i: 0 }, { l: '15+0', m: 15, i: 0 }, { l: '30+0', m: 30, i: 0 }] },
    ]
    const { friendsList } = useUserData();

    const [showModal, setShowModal] = useState(false)
    const [showComputerModal, setShowComputerModal] = useState(false)
    const [friendMode, setFriendMode] = useState('friend'); // 'friend' | 'link'
    const [selectedFriend, setSelectedFriend] = useState('');
    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedOptionv, setSelectedOptionv] = useState(null);
    const [selectedTC, setSelectedTC] = useState(null);
    const [customMin, setCustomMin] = useState('');
    const [customInc, setCustomInc] = useState('');
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [computerSide, setComputerSide] = useState(null);
    const [computerSideLabel, setComputerSideLabel] = useState(null);

    // Matchmaking state
    const [showFindModal, setShowFindModal] = useState(false);
    const [findTC, setFindTC] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [myQueueRating, setMyQueueRating] = useState(null);
    const [findError, setFindError] = useState(null);
    const [createError, setCreateError] = useState(null);

    const searchTimerRef = useRef(null);
    const retryIntervalRef = useRef(null);
    const queueUnsubRef = useRef(null);
    const isSearchingRef = useRef(false);
    useEffect(() => { isSearchingRef.current = isSearching; }, [isSearching]);

    // Cancel queue and clean up on unmount
    useEffect(() => {
        return () => {
            cleanupSearch();
            if (isSearchingRef.current) {
                const uid = auth.currentUser?.uid;
                if (uid) deleteDoc(doc(db, 'matchmakingQueue', uid)).catch(() => {});
            }
        };
    }, []);

    function getCategory(minutes) {
        if (minutes <= 2) return 'bullet';
        if (minutes <= 9) return 'blitz';
        return 'rapid';
    }

    function cleanupSearch() {
        clearTimeout(searchTimerRef.current);
        clearInterval(retryIntervalRef.current);
        if (queueUnsubRef.current) { queueUnsubRef.current(); queueUnsubRef.current = null; }
    }

    async function tryMatch(tc, category, myRating, ignoreRating) {
        try {
            const uid = auth.currentUser?.uid;
            if (!uid) return;

            const snap = await getDocs(query(collection(db, 'matchmakingQueue'), where('category', '==', category)));
            const cutoff = Date.now() - 5 * 60 * 1000;
            let candidates = snap.docs
                .filter(d => {
                    const data = d.data();
                    return data.uid !== uid && data.gameId === null
                        && data.minutes === tc.minutes && data.increment === tc.increment
                        && (data.joinedAt?.toMillis?.() || 0) > cutoff;
                })
                .map(d => ({ ref: d.ref, ...d.data() }));

            if (!ignoreRating) {
                const close = candidates.filter(c => Math.abs(c.rating - myRating) <= 200);
                if (close.length > 0) candidates = close;
                else return;
            }
            if (candidates.length === 0) return;

            candidates.sort((a, b) => (a.joinedAt?.toMillis?.() || 0) - (b.joinedAt?.toMillis?.() || 0));
            const opponent = candidates[0];

            const userSnap = await getDoc(doc(db, 'backenddata', uid));
            const username = userSnap.data()?.username;
            if (!username) return;

            const gameId = `${Math.random().toString(36).substr(2, 9)}_${Date.now()}`;
            const myPiece = Math.random() < 0.5 ? 'w' : 'b';
            const myQueueRef = doc(db, 'matchmakingQueue', uid);

            await runTransaction(db, async (tx) => {
                const oppDoc = await tx.get(opponent.ref);
                if (!oppDoc.exists() || oppDoc.data().gameId !== null) throw new Error('already_matched');
                tx.set(doc(db, 'games', gameId), {
                    game: { gameId, status: 'waiting', members: [{ uid, name: username, piece: myPiece, time: opponent.minutes * 60, creator: true }] },
                    increment: opponent.increment,
                    isRated: true,
                });
                tx.update(myQueueRef, { gameId });
                tx.update(opponent.ref, { gameId });
            });

            cleanupSearch();
            setIsSearching(false);
            deleteDoc(myQueueRef).catch(() => {});
            navigate(`/game/${gameId}`);
        } catch (e) {
            if (e.message !== 'already_matched') console.error('Matchmaking error:', e);
        }
    }

    async function joinQueue() {
        if (!findTC) return;
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        setFindError(null);
        setIsSearching(true);

        const tc = { minutes: findTC.minutes, increment: findTC.increment };
        const category = getCategory(tc.minutes);
        const ratingKey = `${category}Rating`;
        try {
            const userSnap = await getDoc(doc(db, 'backenddata', uid));
            const username = userSnap.data()?.username;
            if (!username) {
                setIsSearching(false);
                setFindError('You need an account with a username to play rated games.');
                return;
            }
            const myRating = userSnap.data()?.[ratingKey] ?? 1200;
            setMyQueueRating(myRating);

            const queueRef = doc(db, 'matchmakingQueue', uid);
            await setDoc(queueRef, { uid, username, category, tc: `${tc.minutes}+${tc.increment}`, minutes: tc.minutes, increment: tc.increment, rating: myRating, joinedAt: serverTimestamp(), gameId: null });

            queueUnsubRef.current = onSnapshot(queueRef, (snap) => {
                const data = snap.data();
                if (data?.gameId) {
                    cleanupSearch();
                    setIsSearching(false);
                    deleteDoc(queueRef).catch(() => {});
                    navigate(`/game/${data.gameId}`);
                }
            });

            await tryMatch(tc, category, myRating, false);
            retryIntervalRef.current = setInterval(() => tryMatch(tc, category, myRating, false), 5000);
            searchTimerRef.current = setTimeout(() => {
                clearInterval(retryIntervalRef.current);
                tryMatch(tc, category, myRating, true);
                retryIntervalRef.current = setInterval(() => tryMatch(tc, category, myRating, true), 5000);
            }, 30000);
        } catch (e) {
            console.error('Failed to join matchmaking queue:', e);
            cleanupSearch();
            setIsSearching(false);
            setFindError('Could not start searching. Check your connection and try again.');
        }
    }

    async function cancelQueue() {
        cleanupSearch();
        setIsSearching(false);
        const uid = auth.currentUser?.uid;
        if (uid) deleteDoc(doc(db, 'matchmakingQueue', uid)).catch(() => {});
    }

    // Auto-open the online modal when coming from a friend challenge
    useEffect(() => {
        if (challengeUsername) {
            setShowModal(true);
            setShowComputerModal(false);
        }
    }, [challengeUsername]);

    async function startOnlineGame(startingPiece, tc, rated = true) {
        setCreateError(null);
        try {
        gametimeH = tc.minutes * 60;
        const usersCollectionRef = collection(db, 'backenddata');
        const currentUser = auth.currentUser;
        const currentUserDocRef = doc(usersCollectionRef, currentUser.uid);
        const currentUserDocSnapshot = await getDoc(currentUserDocRef);
        const username = currentUserDocSnapshot.data()?.username;
        if (!username) {
            setCreateError('Could not load your profile. Please try again.');
            return;
        }
        const member = {
            uid: currentUser.uid,
            piece: startingPiece === 'r' ? ['b', 'w'][Math.round(Math.random())] : startingPiece,
            name: username,
            creator: true,
            time: tc.minutes * 60
        }
        const game = {
            status: 'waiting',
            members: [member],
            gameId: `${Math.random().toString(36).substr(2, 9)}_${Date.now()}`
        }
        await setDoc(doc(db, "games", game.gameId), {
            game: game,
            increment: tc.increment,
            ...(rated ? {} : { isRated: false }),
        })

        const inviteTo = rated ? (challengeUsername || selectedFriend || null) : null;
        let inviteDocId = null;
        if (inviteTo) {
            const friendQ = query(usersCollectionRef, where('username', '==', inviteTo));
            const friendSnap = await getDocs(friendQ);
            if (!friendSnap.empty) {
                const friendUid = friendSnap.docs[0].id;
                const sideLabel = startingPiece === 'w' ? 'White' : startingPiece === 'b' ? 'Black' : 'Random';
                const invDoc = await addDoc(collection(db, 'gameInvites'), {
                    fromUid: currentUser.uid,
                    fromUsername: username,
                    toUid: friendUid,
                    toUsername: inviteTo,
                    gameId: game.gameId,
                    timeControl: `${tc.minutes}+${tc.increment}`,
                    side: sideLabel,
                    status: 'pending',
                });
                inviteDocId = invDoc.id;
            }
        }

        navigate(`/game/${game.gameId}`, { state: { inviteDocId } });
        } catch (e) {
            console.error('Failed to create game:', e);
            setCreateError('Could not create the game. Check your connection and try again.');
        }
    }

    function startLocalGame() {
        const side = computerSide === 'r' ? (Math.random() < 0.5 ? 'w' : 'b') : computerSide;
        navigate('/game/local', { state: { level: selectedLevel, side } });
    }
    function handlePlayComputer() {
        setShowComputerModal(true);
        setShowModal(false);
        setShowFindModal(false);
        if (isSearchingRef.current) cancelQueue();
    }
    function handlePlayOnline() {
        setShowModal(true);
        setShowComputerModal(false);
        setShowFindModal(false);
        if (isSearchingRef.current) cancelQueue();
    }
    function handleFindOpponent() {
        setShowFindModal(true);
        setShowModal(false);
        setShowComputerModal(false);
    }
    
    return (
        <>
            <div>
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    minHeight: '70vh',
                    padding: '40px 12px',
                    maxWidth: '600px',
                    margin: '0 auto',
                    boxSizing: 'border-box',
                    width: '100%',
                }}>
                    <h1 style={{ marginBottom: '30px', textAlign: 'center' }}>Play Chess</h1>

                    <div style={{
                        display: 'flex',
                        gap: '16px',
                        marginBottom: '30px',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                    }}>
                        <button className={styles.custombutton} onClick={handleFindOpponent}
                            style={{ minWidth: '140px', padding: '12px 20px', fontSize: '15px', flex: '1' }}>
                            &#9823; Find Opponent
                        </button>
                        <button className={styles.custombutton} onClick={handlePlayComputer}
                            style={{ minWidth: '140px', padding: '12px 20px', fontSize: '15px', flex: '1' }}>
                            &#9816; Play vs Computer
                        </button>
                        <button className={styles.custombutton} onClick={handlePlayOnline}
                            style={{ minWidth: '140px', padding: '12px 20px', fontSize: '15px', flex: '1' }}>
                            &#9814; Play with Friend
                        </button>
                    </div>

                    {showFindModal && !isSearching && (
                        <div style={{ background: '#162060', border: '3px solid #2a3a8c', borderRadius: '8px', padding: '20px', width: '100%', maxWidth: '400px', boxSizing: 'border-box', boxShadow: '3px 4px 0px #0a1040', marginBottom: '20px' }}>
                            <div style={{ marginBottom: '16px' }}>
                                <p style={{ color: '#ffd700', fontFamily: "'Press Start 2P', cursive", fontSize: '10px', marginBottom: '10px', textTransform: 'uppercase', textShadow: '1px 1px 0 #0a1040' }}>Time Control</p>
                                {timePresets.map(({ cat, opts }) => (
                                    <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                        <span style={{ width: '44px', fontSize: '9px', color: '#8899cc', fontFamily: "'JetBrains Mono', monospace", flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{cat}</span>
                                        {opts.map(opt => {
                                            const active = findTC?.minutes === opt.m && findTC?.increment === opt.i;
                                            return (
                                                <button type="button" className={styles.pickbtn} key={opt.l} onClick={() => setFindTC({ minutes: opt.m, increment: opt.i })} style={{ padding: '7px 12px', border: active ? '2px solid #ffd700' : '2px solid #2a3a8c', borderRadius: '4px', background: active ? '#2a3a8c' : 'transparent', color: active ? '#ffd700' : '#c0ccff', cursor: 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", transition: 'all 0.1s ease' }}>{opt.l}</button>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                            {findError && (
                                <p style={{ color: '#ff5577', fontSize: '12px', marginBottom: '10px' }}>{findError}</p>
                            )}
                            {findTC && (
                                <button onClick={joinQueue} className={styles.custombutton} style={{ width: '100%', padding: '14px', fontSize: '15px' }}>
                                    Find Game &mdash; {findTC.minutes}+{findTC.increment}
                                </button>
                            )}
                        </div>
                    )}

                    {showFindModal && isSearching && (
                        <div style={{ background: '#162060', border: '3px solid #ffd700', borderRadius: '8px', padding: '20px', width: '100%', maxWidth: '400px', boxSizing: 'border-box', boxShadow: '3px 4px 0px #0a1040, 0 0 20px rgba(255,215,0,0.12)', marginBottom: '20px', textAlign: 'center' }}>
                            <GlobeSpinner size={60} />
                            <p style={{ color: '#ffd700', fontFamily: "'Press Start 2P', cursive", fontSize: '9px', marginBottom: '8px', textTransform: 'uppercase', textShadow: '1px 1px 0 #0a1040' }}>Finding opponent...</p>
                            {findTC && (
                                <p style={{ color: '#c0ccff', fontSize: '13px', marginBottom: '4px', fontFamily: "'JetBrains Mono', monospace" }}>
                                    {findTC.minutes}+{findTC.increment} &middot; {getCategory(findTC.minutes)}
                                </p>
                            )}
                            {myQueueRating !== null && (
                                <p style={{ color: '#8899cc', fontSize: '12px', marginBottom: '16px' }}>Your rating: {myQueueRating}</p>
                            )}
                            <button onClick={cancelQueue} className={styles.custombutton} style={{ padding: '10px 24px', fontSize: '13px' }}>Cancel</button>
                        </div>
                    )}

                    {showComputerModal && (
                        <div style={{
                            background: '#162060',
                            border: '3px solid #2a3a8c',
                            borderRadius: '8px',
                            padding: '20px',
                            width: '100%',
                            maxWidth: '460px',
                            boxSizing: 'border-box',
                            boxShadow: '3px 4px 0px #0a1040',
                            marginBottom: '20px',
                        }}>
                            <div style={{ marginBottom: '16px' }}>
                                <p style={{ 
                                    color: '#ffd700', 
                                    fontFamily: "'Press Start 2P', cursive", 
                                    fontSize: '10px', 
                                    marginBottom: '10px',
                                    textTransform: 'uppercase',
                                    textShadow: '1px 1px 0 #0a1040',
                                }}>Choose Side</p>
                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                    {newGameOptions.map(({ label, value }) => (
                                        <button
                                            type="button"
                                            className={styles.pickbtn}
                                            key={value}
                                            onClick={() => { setComputerSideLabel(label); setComputerSide(value); }}
                                            style={{
                                                padding: '8px 14px',
                                                border: computerSide === value ? '2px solid #ffd700' : '2px solid #2a3a8c',
                                                borderRadius: '4px',
                                                background: computerSide === value ? '#2a3a8c' : 'transparent',
                                                color: computerSide === value ? '#ffd700' : '#c0ccff',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                transition: 'all 0.1s ease',
                                            }}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <p style={{ 
                                    color: '#ffd700', 
                                    fontFamily: "'Press Start 2P', cursive", 
                                    fontSize: '10px', 
                                    marginBottom: '10px',
                                    textTransform: 'uppercase',
                                    textShadow: '1px 1px 0 #0a1040',
                                }}>Difficulty</p>
                                <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(115px, 1fr))',
                                    gap: '8px',
                                }}>
                                    {levels.map((lvl) => (
                                        <button
                                            type="button"
                                            className={styles.pickbtn}
                                            key={lvl.label}
                                            onClick={() => setSelectedLevel(lvl)}
                                            style={{
                                                padding: '10px 8px',
                                                border: selectedLevel?.label === lvl.label ? '2px solid #ffd700' : '2px solid #2a3a8c',
                                                borderRadius: '4px',
                                                background: selectedLevel?.label === lvl.label ? '#2a3a8c' : 'transparent',
                                                cursor: 'pointer',
                                                textAlign: 'center',
                                                transition: 'all 0.15s ease',
                                            }}
                                        >
                                            <div style={{ fontSize: '22px', marginBottom: '4px' }}>{lvl.icon}</div>
                                            <div style={{ 
                                                fontSize: '11px', 
                                                fontWeight: 700,
                                                color: selectedLevel?.label === lvl.label ? '#ffd700' : '#c0ccff',
                                                marginBottom: '2px',
                                            }}>{lvl.label}</div>
                                            <div style={{
                                                fontSize: '10px',
                                                color: selectedLevel?.label === lvl.label ? '#ffd700' : '#6677aa',
                                                fontFamily: "'JetBrains Mono', monospace",
                                            }}>
                                                {lvl.elo ? `~${lvl.elo}` : 'MAX'}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {computerSide && selectedLevel && (
                                <button 
                                    onClick={startLocalGame}
                                    className={styles.custombutton}
                                    style={{ width: '100%', padding: '14px', fontSize: '15px' }}
                                >
                                    Play &mdash; {computerSideLabel}, {selectedLevel.label} {selectedLevel.elo ? `(~${selectedLevel.elo})` : ''}
                                </button>
                            )}
                        </div>
                    )}

                    {showModal && (
                        <div style={{
                            background: '#162060',
                            border: challengeUsername ? '3px solid #ffd700' : '3px solid #2a3a8c',
                            borderRadius: '8px',
                            padding: '20px',
                            width: '100%',
                            maxWidth: '400px',
                            boxSizing: 'border-box',
                            boxShadow: challengeUsername
                                ? '4px 5px 0px #0a1040, 0 0 20px rgba(255,215,0,0.15)'
                                : '3px 4px 0px #0a1040',
                        }}>
                            {/* Profile-page challenge: skip mode toggle, friend is pre-selected */}
                            {challengeUsername && (
                                <p style={{ color: '#ffd700', fontFamily: "'Press Start 2P', cursive", fontSize: '10px', marginBottom: '14px', textTransform: 'uppercase', textShadow: '1px 1px 0 #0a1040', textAlign: 'center' }}>
                                    &#9814; Challenge {challengeUsername}
                                </p>
                            )}

                            {/* Sub-mode toggle: Friends | Link */}
                            {!challengeUsername && (
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                                    {[{ key: 'friend', label: '♟ Friends' }, { key: 'link', label: '🔗 Link' }].map(m => (
                                        <button type="button" className={styles.pickbtn} key={m.key} onClick={() => setFriendMode(m.key)} style={{ flex: 1, padding: '8px', textAlign: 'center', cursor: 'pointer', border: friendMode === m.key ? '2px solid #ffd700' : '2px solid #2a3a8c', borderRadius: '4px', background: friendMode === m.key ? '#2a3a8c' : 'transparent', color: friendMode === m.key ? '#ffd700' : '#c0ccff', fontSize: '12px', fontWeight: 600, transition: 'all 0.1s' }}>
                                            {m.label}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Friend dropdown */}
                            {friendMode === 'friend' && !challengeUsername && (
                                <div style={{ marginBottom: '16px' }}>
                                    <p style={{ color: '#ffd700', fontFamily: "'Press Start 2P', cursive", fontSize: '10px', marginBottom: '8px', textTransform: 'uppercase', textShadow: '1px 1px 0 #0a1040' }}>Choose Friend</p>
                                    {friendsList.length === 0 ? (
                                        <p style={{ color: '#8899cc', fontSize: '12px', lineHeight: 1.5 }}>No friends yet &mdash; add some first, or use <span style={{ color: '#ffd700', cursor: 'pointer' }} onClick={() => setFriendMode('link')}>Link mode</span>.</p>
                                    ) : (
                                        <select value={selectedFriend} onChange={e => setSelectedFriend(e.target.value)} style={{ width: '100%', background: '#0d1654', border: '2px solid #2a3a8c', borderRadius: '4px', color: selectedFriend ? '#e8eeff' : '#8899cc', padding: '8px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', outline: 'none', cursor: 'pointer' }}>
                                            <option value="">— Select a friend —</option>
                                            {friendsList.map(f => <option key={f.username} value={f.username}>{f.username}</option>)}
                                        </select>
                                    )}
                                </div>
                            )}

                            {/* Link mode info */}
                            {friendMode === 'link' && !challengeUsername && (
                                <div style={{ marginBottom: '16px', padding: '10px 12px', background: '#0d1654', border: '1px solid #3344aa', borderRadius: '4px', textAlign: 'center' }}>
                                    <p style={{ color: '#c0ccff', fontSize: '12px', marginBottom: '2px' }}>Share a link &mdash; anyone can join without an account</p>
                                    <p style={{ color: '#8899cc', fontSize: '11px' }}>Unrated game</p>
                                </div>
                            )}

                            {/* Side selector */}
                            <div style={{ marginBottom: '16px' }}>
                                <p style={{ color: '#ffd700', fontFamily: "'Press Start 2P', cursive", fontSize: '10px', marginBottom: '10px', textTransform: 'uppercase', textShadow: '1px 1px 0 #0a1040' }}>Choose Side</p>
                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                    {newGameOptions.map(({ label, value }) => (
                                        <button type="button" className={`${styles.selected} ${styles.pickbtn}`} key={value} onClick={() => { setSelectedOption(label); setSelectedOptionv(value); }}
                                            style={{ padding: '8px 14px', border: selectedOptionv === value ? '2px solid #ffd700' : '2px solid #2a3a8c', borderRadius: '4px', background: selectedOptionv === value ? '#2a3a8c' : 'transparent', color: selectedOptionv === value ? '#ffd700' : '#c0ccff', cursor: 'pointer', fontSize: '13px', fontWeight: 600, transition: 'all 0.1s ease' }}>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Time control */}
                            <div style={{ marginBottom: '20px' }}>
                                <p style={{ color: '#ffd700', fontFamily: "'Press Start 2P', cursive", fontSize: '10px', marginBottom: '10px', textTransform: 'uppercase', textShadow: '1px 1px 0 #0a1040' }}>Time Control</p>
                                {timePresets.map(({ cat, opts }) => (
                                    <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                        <span style={{ width: '44px', fontSize: '9px', color: '#8899cc', fontFamily: "'JetBrains Mono', monospace", flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{cat}</span>
                                        {opts.map(opt => {
                                            const active = selectedTC?.fromPreset && selectedTC.minutes === opt.m && selectedTC.increment === opt.i;
                                            return (
                                                <button type="button" className={styles.pickbtn} key={opt.l} onClick={() => { setSelectedTC({ minutes: opt.m, increment: opt.i, fromPreset: true }); setCustomMin(''); setCustomInc(''); }}
                                                    style={{ padding: '7px 12px', border: active ? '2px solid #ffd700' : '2px solid #2a3a8c', borderRadius: '4px', background: active ? '#2a3a8c' : 'transparent', color: active ? '#ffd700' : '#c0ccff', cursor: 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", transition: 'all 0.1s ease' }}>
                                                    {opt.l}
                                                </button>
                                            );
                                        })}
                                    </div>
                                ))}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
                                    <span style={{ width: '44px', fontSize: '9px', color: '#8899cc', fontFamily: "'JetBrains Mono', monospace", flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Custom</span>
                                    <input type="number" min="1" max="180" placeholder="min" value={customMin}
                                        onChange={e => { setCustomMin(e.target.value); const m = parseInt(e.target.value); const i = parseInt(customInc) || 0; if (!isNaN(m) && m > 0) setSelectedTC({ minutes: m, increment: i, fromPreset: false }); else setSelectedTC(null); }}
                                        style={{ width: '50px', background: '#0d1654', border: '2px solid #2a3a8c', borderRadius: '4px', color: '#e8eeff', padding: '5px 6px', fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', outline: 'none', textAlign: 'center' }} />
                                    <span style={{ color: '#8899cc', fontSize: '11px', fontFamily: "'JetBrains Mono', monospace" }}>min +</span>
                                    <input type="number" min="0" max="60" placeholder="0" value={customInc}
                                        onChange={e => { setCustomInc(e.target.value); const m = parseInt(customMin) || 0; const i = parseInt(e.target.value); if (!isNaN(m) && m > 0) setSelectedTC({ minutes: m, increment: isNaN(i) ? 0 : Math.max(0, i), fromPreset: false }); }}
                                        style={{ width: '44px', background: '#0d1654', border: '2px solid #2a3a8c', borderRadius: '4px', color: '#e8eeff', padding: '5px 6px', fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', outline: 'none', textAlign: 'center' }} />
                                    <span style={{ color: '#8899cc', fontSize: '11px', fontFamily: "'JetBrains Mono', monospace" }}>sec/move</span>
                                </div>
                            </div>

                            {createError && (
                                <p style={{ color: '#ff5577', fontSize: '12px', marginBottom: '10px' }}>{createError}</p>
                            )}
                            {selectedOptionv && selectedTC && (friendMode === 'link' || challengeUsername || selectedFriend) && (
                                <button onClick={() => startOnlineGame(selectedOptionv, selectedTC, friendMode !== 'link')} className={styles.custombutton} style={{ width: '100%', padding: '14px', fontSize: '15px' }}>
                                    {friendMode === 'link'
                                        ? <>Create Link &mdash; {selectedOption}, {selectedTC.minutes}+{selectedTC.increment}</>
                                        : <>Send Challenge &mdash; {selectedOption}, {selectedTC.minutes}+{selectedTC.increment}</>
                                    }
                                </button>
                            )}
                        </div>
                    )}

                    {!showModal && !showComputerModal && !showFindModal && (
                        <div style={{
                            background: '#162060',
                            border: '3px solid #2a3a8c',
                            borderRadius: '8px',
                            padding: '20px',
                            width: '100%',
                            maxWidth: '400px',
                            boxSizing: 'border-box',
                            textAlign: 'center',
                            boxShadow: '3px 4px 0px #0a1040',
                        }}>
                            <p style={{ 
                                color: '#ffd700', 
                                fontFamily: "'Press Start 2P', cursive", 
                                fontSize: '10px', 
                                marginBottom: '12px',
                                textShadow: '1px 1px 0 #0a1040',
                            }}>How to play</p>
                            <p style={{ color: '#8899cc', fontSize: '14px', lineHeight: '1.6' }}>
                                <strong style={{ color: '#c0ccff' }}>vs Computer</strong> — Choose a difficulty and play against Stockfish AI<br/>
                                <strong style={{ color: '#c0ccff' }}>Online</strong> — Create a game and share the link with a friend
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
    
}
